import { getOrdinalSuffix } from '@/src/common/utils/date'
import { ScheduledTransfers } from '@/src/experiences/card-activity/components/feed/_highnote'
import type { AutoPayScheduleComponentType } from '@/src/experiences/card-payments/screens/AutoPaySchedule'
import { TransferBalanceAmountCode } from '@api/mesa/graphql'
import { useState, useMemo } from 'react'
import {
  type LegacyAutoPayStore,
  useLegacyAutoPayStore,
} from '@/src/experiences/card-payments/store/useLegacyAutoPayStore'
import { DropdownComponent } from '@/src/common/components/Dropdown'
import { CardPaymentsE2E } from '@/src/experiences/card-payments/e2e'
import { OptionItemSelector } from '@/src/app/main/(tabs)/card/card-management/payment/optionItemSelector'
import { PaymentCardInformation } from '@/src/app/main/(tabs)/card/card-management/_common/paymentCardInformation'
import { useLegacyCard } from '@/src/common/hooks/useCardInformation'

const allDays = Array.from({ length: 24 }, (_, i) => i + 3).map((value) => ({
  label: `${value}${getOrdinalSuffix(value)} of the month`,
  value,
}))

const paymentStrategies: NonNullable<LegacyAutoPayStore['paymentStrategy']>[] = [
  {
    value: TransferBalanceAmountCode.MinimumPayment,
    label: ScheduledTransfers.calculatedTypeText.MinimumPayment,
  },
  {
    value: TransferBalanceAmountCode.OutstandingStatementBalance,
    label: ScheduledTransfers.calculatedTypeText.OutstandingStatementBalance,
  },
  {
    value: TransferBalanceAmountCode.OutstandingBalance,
    label: ScheduledTransfers.calculatedTypeText.OutstandingBalance,
  },
]

export function LegacyAutoPaySchedule({
  AutoPayScheduleComponent,
}: {
  AutoPayScheduleComponent: AutoPayScheduleComponentType
}) {
  const autoPayStore = useLegacyAutoPayStore()
  const [paymentStrategy, setPaymentStrategy] = useState<LegacyAutoPayStore['paymentStrategy']>()
  const [selectedDay, setSelectedDay] = useState<{ label: string; value: number }>()

  const cardInformation = useLegacyCard().data?.getCreditCardInformation.node
  const closingDateValue = cardInformation?.financialAccount.paymentCardNextClosingDate?.toString()

  const availableDays = useMemo(() => {
    if (!closingDateValue) {
      return allDays
    }

    const closingDate = new Date(closingDateValue)
    // Highnote uses UTC dates, so we need to get the UTC date
    const closingDay = closingDate.getUTCDate()

    // Block 5 days before, the closing day itself, and 1 day after. This is because:
    // - 5 days before: To avoid scheduling payments early where balance is not finalized, paid off early, more balance is added to the statement, and is not paid off in the next 25 days (default grace period), resulting in late payment.
    // - The closing day itself: To avoid scheduling payments on the day of the statement close, since highnote processes payments 4 hours earlier before statement close on the same day (allowing for same case as above with 4 hour danger period)
    // - 1 day after: Recommended by highnote to avoid scheduling payments too close to the next statement close to give some buffer time for statement to close
    return allDays.filter((day) => day.value < closingDay - 5 || day.value > closingDay + 1)
  }, [closingDateValue])

  return (
    <AutoPayScheduleComponent
      recurringPaymentStore={autoPayStore}
      paymentStrategies={paymentStrategies}
      paymentStrategy={paymentStrategy}
      setPaymentStrategy={setPaymentStrategy}
      day={selectedDay}
      setDay={setSelectedDay}
      paymentStrategySelector={
        <PaymentCardInformation title="Payment Amount" containerStyle={{ marginHorizontal: 16 }}>
          {paymentStrategies.map((item, index) => (
            <OptionItemSelector
              onItemPress={() => setPaymentStrategy(item)}
              withBorder={index !== paymentStrategies.length - 1}
              key={item.value}
              title={item.label}
              isSelected={paymentStrategy?.value === item.value}
            />
          ))}
        </PaymentCardInformation>
      }
      daySelector={
        <DropdownComponent
          maxHeight={200}
          style={{ borderWidth: 0 }}
          placeholderStyle={{ color: '#C1C4C6', marginLeft: 0 }}
          value={selectedDay}
          onChange={setSelectedDay}
          testID={CardPaymentsE2E.Ids.Input.SelectAutoPayDayOfTheMonth}
          placeholder="Select Auto-pay day of the month"
          data={availableDays}
          labelField="label"
          valueField="value"
        />
      }
    />
  )
}
